import React, { useState, useEffect } from 'react';
import { Eye, Upload, Camera, FileText, ShieldCheck, FileImage, FileCheck2, Image } from 'lucide-react';
import UploadModal from './UploadModal';
import { getLabImgDataById, getLabGalleryDataById } from '../../apiCalls/user';
import { Link } from 'react-router-dom';

/* ── helpers ──────────────────────────────── */
const VerificationBadge = ({ status }) => {
  const verified = `${status}`.toLowerCase() === 'verified';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.2rem 0.6rem', borderRadius: '9999px',
      fontSize: '0.68rem', fontWeight: 700,
      background: verified ? 'var(--success-light)' : 'var(--warning-light)',
      color:      verified ? '#065f46'               : '#92400e',
      textTransform: 'uppercase', letterSpacing: '0.4px',
      whiteSpace: 'nowrap',
    }}>
      <i className={`bi bi-${verified ? 'shield-fill-check' : 'clock'}`} style={{ fontSize: '0.65rem' }} />
      {verified ? 'Verified' : (status || 'Pending')}
    </span>
  );
};

/* single document row */
const DocRow = ({ icon, label, verificationStatus, uploadKey, fileUrl, onUpload, hideUpload }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.875rem 1rem',
    borderBottom: '1px solid var(--gray-50)',
    transition: 'background 0.15s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 'var(--radius-md)',
        background: 'var(--primary-light)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--gray-800)', marginBottom: 3 }}>
          {label}
        </div>
        {verificationStatus !== undefined && (
          <VerificationBadge status={verificationStatus} />
        )}
      </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, marginLeft: '1rem' }}>
      {!hideUpload && (
        <button
          className="app-btn app-btn--outline-primary app-btn--sm"
          onClick={() => onUpload(uploadKey)}
          title="Upload"
        >
          <Upload size={13} /> Upload
        </button>
      )}
      {fileUrl && (
        <Link to={fileUrl} target="_blank" rel="noreferrer">
          <button className="app-btn app-btn--ghost app-btn--sm" title="View document">
            <Eye size={13} /> View
          </button>
        </Link>
      )}
      {!fileUrl && (
        <button className="app-btn app-btn--ghost app-btn--sm" disabled style={{ opacity: 0.4, cursor: 'not-allowed' }}>
          <Eye size={13} /> View
        </button>
      )}
    </div>
  </div>
);

/* single gallery photo card */
const PhotoCard = ({ label, icon, photoUrl, galleryKey, onUpload }) => (
  <div style={{
    background: 'var(--white)', border: '1px solid var(--gray-100)',
    borderRadius: 'var(--radius-lg)', overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(129,102,244,0.06)',
    transition: 'box-shadow 0.18s, transform 0.18s',
  }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(129,102,244,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(129,102,244,0.06)'; e.currentTarget.style.transform = 'none'; }}
  >
    {/* Preview area */}
    <div style={{
      height: 110, background: photoUrl ? 'var(--gray-200)' : 'var(--primary-light)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', position: 'relative',
    }}>
      {photoUrl ? (
        <img src={photoUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{ color: 'var(--primary)', opacity: 0.5 }}>{icon}</div>
      )}
      {photoUrl && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0, transition: 'opacity 0.18s',
        }}
          className="photo-overlay"
        >
          <Link to={photoUrl} target="_blank" rel="noreferrer">
            <button style={{
              background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%',
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--primary)',
            }}>
              <Eye size={16} />
            </button>
          </Link>
        </div>
      )}
    </div>

    {/* Footer */}
    <div style={{ padding: '0.65rem 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-700)' }}>{label}</span>
      <button
        className="app-btn app-btn--outline-primary app-btn--sm"
        onClick={() => onUpload(galleryKey)}
        style={{ padding: '0.28rem 0.6rem', fontSize: '0.72rem' }}
      >
        <Upload size={12} />
      </button>
    </div>
  </div>
);

/* ── Main component ───────────────────────── */
const ProfileDocTab = ({ labId }) => {
  const [modalOpen,   setModalOpen]   = useState(false);
  const [uploadType,  setUploadType]  = useState('');
  const [isGallery,   setIsGallery]   = useState(false);
  const [labDocData,  setLabDocData]  = useState({});
  const [labGallery,  setLabGallery]  = useState({});
  const [refreshKey,  setRefreshKey]  = useState(0);

  useEffect(() => {
    getLabImgDataById(labId).then(setLabDocData);
    getLabGalleryDataById(labId).then(setLabGallery);
  }, [labId, refreshKey]);

  const openDocUpload = (type)     => { setUploadType(type); setIsGallery(false); setModalOpen(true); };
  const openGalleryUpload = (type) => { setUploadType(type); setIsGallery(true);  setModalOpen(true); };
  const onUploadSuccess = ()       => { setModalOpen(false); setRefreshKey(k => k + 1); };

  const docs = [
    {
      label: 'Main Photo',
      icon: <FileImage size={18} style={{ color: 'var(--primary)' }} />,
      uploadKey: 'main-img',
      fileUrl: labDocData?.mainImg,
      verificationStatus: undefined,
      hideUpload: false,
    },
    {
      label: 'Medical License',
      icon: <ShieldCheck size={18} style={{ color: 'var(--primary)' }} />,
      uploadKey: 'medical-license',
      fileUrl: labDocData?.medicalLicense,
      verificationStatus: labDocData?.medicalVerification,
      hideUpload: labDocData?.medicalVerification === 'verified',
    },
    {
      label: 'Insurance Certificate',
      icon: <FileCheck2 size={18} style={{ color: 'var(--primary)' }} />,
      uploadKey: 'insurance-certificate',
      fileUrl: labDocData?.insuranceCertificate,
      verificationStatus: labDocData?.insuranceVerification,
      hideUpload: labDocData?.insuranceVerification === 'verified',
    },
    {
      label: 'Registration Certificate',
      icon: <FileText size={18} style={{ color: 'var(--primary)' }} />,
      uploadKey: 'reg-certificate',
      fileUrl: labDocData?.registrationCertificate,
      verificationStatus: labDocData?.registrationVerification,
      hideUpload: labDocData?.registrationVerification === 'verified',
    },
  ];

  const galleryItems = [
    { label: 'Reception',  key: 'reception-area',  url: labGallery?.receptionArea  },
    { label: 'Testing Lab',key: 'testing-lab',      url: labGallery?.testingLab     },
    { label: 'Equipment',  key: 'equipment-area',   url: labGallery?.equimpmentArea },
    { label: 'Waiting Area',key: 'waiting-area',    url: labGallery?.waitingArea    },
    { label: 'Exterior',   key: 'exterior-area',    url: labGallery?.exteriorView   },
  ];

  const verifiedCount = docs.filter(d =>
    d.verificationStatus !== undefined && `${d.verificationStatus}`.toLowerCase() === 'verified'
  ).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ── Summary strip ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '1rem' }}>
        {[
          { label: 'Documents',     value: docs.length,           icon: 'bi-file-earmark-text', color: '#8166f4', bg: '#f3f0ff' },
          { label: 'Verified',      value: verifiedCount,          icon: 'bi-shield-fill-check', color: '#10b981', bg: '#d1fae5' },
          { label: 'Pending',       value: docs.filter(d => d.verificationStatus && d.verificationStatus !== 'verified').length, icon: 'bi-clock', color: '#f59e0b', bg: '#fef3c7' },
          { label: 'Gallery Photos',value: galleryItems.filter(g => g.url).length + ' / ' + galleryItems.length, icon: 'bi-images', color: '#3b82f6', bg: '#dbeafe' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', border: '1px solid var(--gray-100)',
            borderRadius: 'var(--radius-lg)', padding: '0.875rem 1rem',
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            boxShadow: '0 2px 10px rgba(129,102,244,0.05)',
          }}>
            <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-md)', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className={`bi ${s.icon}`} style={{ fontSize: '1rem', color: s.color }} />
            </div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-900)', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginTop: 3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Two column grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

        {/* Documents card */}
        <div className="form-card">
          <div className="form-card-header">
            <h5 className="form-card-title">
              <FileText size={15} /> Legal Documents
            </h5>
            <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
              {verifiedCount}/{docs.length} verified
            </span>
          </div>
          <div style={{ padding: '0.5rem 0' }}>
            {docs.map(doc => (
              <DocRow key={doc.uploadKey} {...doc} onUpload={openDocUpload} />
            ))}
          </div>
        </div>

        {/* Gallery card */}
        <div className="form-card">
          <div className="form-card-header">
            <h5 className="form-card-title">
              <Camera size={15} /> Lab Gallery
            </h5>
            <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
              {galleryItems.filter(g => g.url).length}/{galleryItems.length} uploaded
            </span>
          </div>
          <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {galleryItems.map(item => (
              <PhotoCard
                key={item.key}
                label={item.label}
                galleryKey={item.key}
                photoUrl={item.url}
                icon={<Camera size={32} />}
                onUpload={openGalleryUpload}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Upload modal */}
      <UploadModal
        labId={labId}
        show={modalOpen}
        uploadType={uploadType}
        isGallery={isGallery}
        onClose={() => setModalOpen(false)}
        onSuccess={onUploadSuccess}
      />
    </div>
  );
};

export default ProfileDocTab;
